import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Check, X, BellRing, CalendarClock, CheckCircle2 } from "lucide-react";
import { api, errMsg, dOnly, VISIT_STATUS } from "@/lib/api";
import { PageHeader, Sel, Field, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

function VisitDialog({ visit, onClose, onSaved }) {
  const [leads, setLeads] = useState([]);
  const [props, setProps] = useState([]);
  const [f, setF] = useState({ lead_id: visit?.lead_id || "", property_code: visit?.property_code || "", date: visit?.date || "", time: visit?.time || "", notes: visit?.notes || "" });
  const [slots, setSlots] = useState([]);
  useEffect(() => { api.get("/leads").then((r) => setLeads(r.data)); api.get("/properties", { params: { status: "disponivel" } }).then((r) => setProps(r.data)); }, []);
  useEffect(() => {
    if (f.property_code && f.date) api.get("/visits/slots", { params: { property_code: f.property_code, date: f.date } }).then((r) => setSlots(r.data.horarios || []));
  }, [f.property_code, f.date]);
  const save = async () => {
    try {
      if (visit?.id) await api.patch(`/visits/${visit.id}`, { date: f.date, time: f.time, notes: f.notes });
      else await api.post("/visits", f);
      toast.success(visit?.id ? "Visita remarcada" : "Visita agendada"); onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-background">
        <DialogHeader><DialogTitle>{visit?.id ? "Remarcar visita" : "Agendar visita"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          {!visit?.id && <Field label="Cliente" className="col-span-2"><Sel value={f.lead_id} onChange={(v) => setF({ ...f, lead_id: v })} placeholder="Selecione" options={Object.fromEntries(leads.map((l) => [l.id, `${l.name || "+" + l.phone}`]))} testid="visit-lead-select" className="w-full" /></Field>}
          {!visit?.id && <Field label="Imóvel" className="col-span-2"><Sel value={f.property_code} onChange={(v) => setF({ ...f, property_code: v })} placeholder="Selecione" options={Object.fromEntries(props.map((p) => [p.code, `${p.code} — ${p.title}`]))} testid="visit-property-select" className="w-full" /></Field>}
          <Field label="Data"><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value, time: "" })} data-testid="visit-date-input" /></Field>
          <Field label="Horário disponível"><Sel value={f.time} onChange={(v) => setF({ ...f, time: v })} placeholder={slots.length ? "Selecione" : "Sem horários"} options={Object.fromEntries(slots.map((s) => [s, s]))} testid="visit-time-select" className="w-full" /></Field>
          <Field label="Observações" className="col-span-2"><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="visit-notes-input" /></Field>
        </div>
        <p className="text-xs text-muted-foreground">Somente horários livres conforme a configuração da imobiliária e a agenda do corretor.</p>
        <DialogFooter><Button onClick={save} disabled={!f.time} data-testid="visit-save-button">Salvar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Visits() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [dlg, setDlg] = useState(null);
  const load = () => api.get("/visits", { params: { status } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line
  const upd = async (v, body, msg) => { try { await api.patch(`/visits/${v.id}`, body); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  const remind = async (v) => { try { await api.post(`/visits/${v.id}/remind`); toast.success("Lembrete enviado"); } catch (e) { toast.error(errMsg(e)); } };
  const grouped = items.reduce((acc, v) => ((acc[v.date] = acc[v.date] || []).push(v), acc), {});
  return (
    <div className="animate-in fade-in duration-500">
      <PageHeader title="Agenda de visitas" subtitle="Horários validados contra a disponibilidade cadastrada — a IA nunca inventa horários.">
        <Sel value={status} onChange={setStatus} placeholder="Todos os status" options={VISIT_STATUS} testid="visits-status-filter" />
        <Button onClick={() => setDlg({})} data-testid="new-visit-button"><Plus className="h-4 w-4 mr-2" />Agendar visita</Button>
      </PageHeader>
      {items.length === 0 && <Empty text="Nenhuma visita" />}
      <div className="space-y-8">
        {Object.entries(grouped).map(([date, vs]) => (
          <div key={date}>
            <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><CalendarClock className="h-4 w-4 text-accent" />{dOnly(date)}</h3>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
              {vs.map((v) => (
                <div key={v.id} className="card-lux" data-testid={`visit-card-${v.id}`}>
                  <div className="flex justify-between items-start"><span className="font-mono text-2xl font-bold">{v.time}</span><span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded bg-secondary" data-testid="visit-status">{VISIT_STATUS[v.status]}{v.confirmed ? " ✓" : ""}</span></div>
                  <div className="mt-2 font-medium">{v.lead_name}</div>
                  <div className="text-sm text-muted-foreground">{v.property_code} — {v.property_title}</div>
                  <div className="text-xs text-muted-foreground mt-1">Corretor: {v.broker_name || "—"} · por {v.created_by}</div>
                  {v.notes && <div className="text-xs mt-2 italic">{v.notes}</div>}
                  {!["cancelada", "concluida"].includes(v.status) && (
                    <div className="flex flex-wrap gap-1.5 mt-4">
                      {!v.confirmed && <Button size="sm" variant="outline" onClick={() => upd(v, { confirmed: true }, "Visita confirmada")} data-testid="visit-confirm"><Check className="h-3.5 w-3.5 mr-1" />Confirmar</Button>}
                      <Button size="sm" variant="outline" onClick={() => setDlg(v)} data-testid="visit-reschedule">Remarcar</Button>
                      <Button size="sm" variant="outline" onClick={() => upd(v, { status: "concluida" }, "Visita concluída")} data-testid="visit-complete"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Concluir</Button>
                      <Button size="sm" variant="ghost" onClick={() => remind(v)} data-testid="visit-remind"><BellRing className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => upd(v, { status: "cancelada" }, "Visita cancelada")} data-testid="visit-cancel"><X className="h-3.5 w-3.5" /></Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {dlg && <VisitDialog visit={dlg.id ? dlg : null} onClose={() => setDlg(null)} onSaved={load} />}
    </div>
  );
}
